import QuizScreenGraphOrganizer, {
  EnrichedData,
  Pointer,
  PointerWithOffset,
  ptr,
} from '@/features/quiz/quiz-screen-graph-organizer'
import { QuizAnswer, QuizAnswerMap, QuizState } from '@/lib/apis/types'
import { AUTHENTICATION_KEY } from '@/features/quiz/constants'
import {
  type QuizScreen,
  ED_CONFIDENCE_LEDGER_KEY,
  HEALTH_IDENTIFIER_DEFERRED_KEY,
  WL_CONSISTENCY_LEDGER_KEY,
} from '@/features/quiz/quiz-screen'

export class TreatmentScreenGraphOrganizer extends QuizScreenGraphOrganizer {
  private readonly deferredPhotoScreenIds: Set<string>

  constructor(
    screenGroups: Array<Array<QuizScreen>>,
    treatmentType?: 'ed' | 'pe' | 'mhl' | 'wl',
  ) {
    const ledgerId =
      treatmentType === 'wl'
        ? WL_CONSISTENCY_LEDGER_KEY
        : treatmentType === 'ed'
          ? ED_CONFIDENCE_LEDGER_KEY
          : undefined
    super(
      ledgerId &&
        !screenGroups
          .flat()
          .some((screen) => screen.type === 'generic' && screen.id === ledgerId)
        ? screenGroups.map((group) =>
            group.flatMap((screen): QuizScreen[] =>
              screen.type === 'generic' && screen.id === 'BillingDetails'
                ? [
                    {
                      type: 'generic',
                      id: ledgerId,
                      displayCriteria: screen.displayCriteria,
                    },
                    screen,
                  ]
                : [screen],
            ),
          )
        : screenGroups,
    )
    // Keep photo screens and their saved answers; only their visibility changes.
    this.deferredPhotoScreenIds = new Set(
      treatmentType === 'wl'
        ? screenGroups
            .flat()
            .flatMap((screen) =>
              screen.type === 'single-question' &&
              screen.question.type === 'photo-upload' &&
              (screen.question.renderId === 'FullBodyPhoto' ||
                screen.question.renderId === 'IDVerification')
                ? [screen.question.id]
                : [],
            )
        : [],
    )
  }

  initialize(loggedIn: boolean, state?: QuizState): PointerWithOffset {
    this.initialized = true
    this.refresh(loggedIn, state?.answers ?? {})

    let groupIdx = 0
    let screenIdx = -1 // `nextScreen` function below will take care of this
    let offset = 0

    const [nextGroupIdx, nextScreenIdx] = this.nextEnabledScreen([
      groupIdx,
      screenIdx,
    ])

    groupIdx = nextGroupIdx
    screenIdx = nextScreenIdx

    let screen = this.groups[nextGroupIdx][nextScreenIdx].screen
    let screenId = screen.type === 'generic' ? screen.id : screen.question.id

    if (state !== undefined) {
      const answers = state?.answers ?? {}

      // Go to the furthest screen, i.e., skip over all the questions the user
      // has answered
      while (
        (screenId in answers &&
          this.questionIsCompleted(screenId, answers[screenId])) ||
        // Existing patients who already finished billing can resume checkout.
        ((screenId === WL_CONSISTENCY_LEDGER_KEY ||
          screenId === ED_CONFIDENCE_LEDGER_KEY) &&
          answers.BillingDetails?.type === 'billing-details' &&
          answers.BillingDetails.completed === true) ||
        // Patients who chose to add their Medicare/IHI after payment.
        (screenId === 'MedicareCard' &&
          answers[HEALTH_IDENTIFIER_DEFERRED_KEY] !== undefined)
      ) {
        const [nextGroupIdx, nextScreenIdx] = this.nextEnabledScreen([
          groupIdx,
          screenIdx,
        ])

        groupIdx = nextGroupIdx
        screenIdx = nextScreenIdx
        offset += 1
        screen = this.groups[nextGroupIdx][nextScreenIdx].screen
        screenId = screen.type === 'generic' ? screen.id : screen.question.id
      }
    }

    return { pointer: ptr(screenId, groupIdx, screenIdx), offset, screen }
  }

  restore(
    loggedIn: boolean,
    state?: QuizState,
    position?: Pick<PointerWithOffset, 'pointer' | 'offset'>,
  ): PointerWithOffset {
    const fallback = this.initialize(loggedIn, state)
    if (!position) return fallback

    const { screenId } = position.pointer
    // Resolve the stable ID: inserting a screen can shift a saved array index.
    const groupIdx = this.groups.findIndex((group) =>
      group.some(
        ({ screen }) =>
          (screen.type === 'generic' ? screen.id : screen.question.id) ===
          screenId,
      ),
    )
    if (groupIdx < 0) return fallback
    const screenIdx = this.groups[groupIdx].findIndex(
      ({ screen }) =>
        (screen.type === 'generic' ? screen.id : screen.question.id) ===
        screenId,
    )
    const storedScreen = this.groups[groupIdx][screenIdx]
    const pointer = ptr(screenId, groupIdx, screenIdx)

    if (storedScreen.disabled) {
      return this.next(
        {
          pointer,
          offset: position.offset,
          screen: storedScreen.screen,
        },
        state?.answers ?? {},
        { authenticated: loggedIn, data: false },
      )
    }

    return {
      pointer: ptr(screenId, groupIdx, screenIdx),
      offset: position.offset,
      screen: storedScreen.screen,
    }
  }

  questionIsCompleted(screenId: string, answer: QuizAnswer): boolean {
    switch (screenId) {
      case 'BillingDetails':
        if (answer.type !== 'billing-details') {
          throw new Error('Unexpected answer type')
        }

        return answer.completed === true
      default:
        if (answer.type === 'photo-upload') {
          return answer.photos.length > 0
        }

        if (answer.type === 'file-upload') {
          return answer.files.length > 0
        }

        // Text screens save drafts while the patient types, so a blank draft
        // is not an answer. The story validation rejects blank text too.
        if (answer.type === 'text') {
          return answer.answer.trim().length > 0
        }

        return true
    }
  }

  next(
    current: PointerWithOffset,
    answers: QuizAnswerMap,
    interacted: EnrichedData<boolean>,
  ): PointerWithOffset {
    this.updateGroup(current.pointer, answers, interacted)

    const [nextGroupIdx, nextScreenIdx] = this.nextEnabledScreen([
      current.pointer.groupIdx,
      current.pointer.screenIdx,
    ])

    // For authentication page, user NEEDS to sign in to go to the next page.
    // And after signing in, the authentication screen is no longer necessary.
    // So we don't change the offset.
    let offset = current.offset + 1
    if (current.pointer.screenId === AUTHENTICATION_KEY) {
      offset = current.offset
    }

    const screen = this.groups[nextGroupIdx][nextScreenIdx].screen
    const screenId = screen.type === 'generic' ? screen.id : screen.question.id

    return {
      pointer: ptr(screenId, nextGroupIdx, nextScreenIdx),
      offset,
      screen,
    }
  }

  prev(current: PointerWithOffset): PointerWithOffset {
    let nextGroupIdx = current.pointer.groupIdx
    let nextScreenIdx = current.pointer.screenIdx - 1

    // Jump to the next group since we're already out of bound
    if (nextScreenIdx < 0) {
      nextGroupIdx -= 1
      nextScreenIdx = this.groups[nextGroupIdx].length - 1
    }

    while (this.groups[nextGroupIdx][nextScreenIdx].disabled) {
      nextScreenIdx -= 1

      if (nextScreenIdx < 0) {
        nextGroupIdx -= 1
        nextScreenIdx = this.groups[nextGroupIdx].length - 1
      }
    }

    const screen = this.groups[nextGroupIdx][nextScreenIdx].screen
    const screenId = screen.type === 'generic' ? screen.id : screen.question.id

    return {
      pointer: ptr(screenId, nextGroupIdx, nextScreenIdx),
      offset: current.offset - 1,
      screen,
    }
  }

  refresh(loggedIn: boolean, answers: QuizAnswerMap): void {
    for (const group of this.groups) {
      for (const screen of group) {
        const id =
          screen.screen.type === 'single-question'
            ? screen.screen.question.id
            : screen.screen.id
        screen.disabled = !this.shouldEnableScreenUsingId(id, loggedIn, answers)
      }
    }
  }

  // Update the group starting from the given pointer based on the answers provided
  // by the user. We only need to go in the forward direction as the previous
  // questions will never change.
  private updateGroup(
    current: Pointer,
    answers: QuizAnswerMap,
    interacted: EnrichedData<boolean>,
  ) {
    const group = this.groups[current.groupIdx]

    // Only update if the user interacted with the quiz.
    if (interacted.data) {
      for (let i = current.screenIdx; i < group.length; i += 1) {
        const screen = group[i].screen
        const id =
          screen.type === 'single-question' ? screen.question.id : screen.id
        group[i].disabled = !this.shouldEnableScreenUsingId(
          id,
          interacted.authenticated,
          answers,
        )
      }
    }
  }

  private shouldEnableScreenUsingId(
    screenId: string,
    authenticated: boolean,
    answers?: QuizAnswerMap,
  ): boolean {
    if (this.deferredPhotoScreenIds.has(screenId)) return false

    const fn = this.criteriaMap[screenId]!
    return fn(authenticated, answers)
  }
}
